import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Trash2, Eye, ShoppingCart, Loader2, AlertTriangle, Pencil, Check as CheckIcon, Truck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

function PriceIndicator({ product, t }) {
  if (!product.cheapest_competitor || !product.price) return null;
  const cheapest = product.cheapest_competitor.price;
  const diff = ((product.price - cheapest) / cheapest) * 100;
  
  let color, label;
  if (diff < -10) { color = "bg-blue-500"; label = t("products.indicator_too_cheap"); }
  else if (diff <= 5) { color = "bg-emerald-500"; label = t("products.indicator_good"); }
  else if (diff <= 20) { color = "bg-amber-500"; label = t("products.indicator_medium"); }
  else { color = "bg-red-500"; label = t("products.indicator_expensive"); }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger><div className={`w-3 h-3 rounded-full ${color} shrink-0`} /></TooltipTrigger>
        <TooltipContent side="right"><p className="text-xs max-w-[200px]">{label}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}


function SellingPriceCell({ product, onSaved, t }) {
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState(product.price || "");
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/products/${product.id}`, { price: parseFloat(price) || 0 });
      toast.success(t("toast.selling_price_updated"));
      setOpen(false);
      if (onSaved) onSaved();
    } catch { toast.error(t("common.error")); }
    finally { setSaving(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="font-mono-data text-sm flex items-center gap-1 px-1 py-0.5 rounded hover:bg-accent/50" data-testid={`sell-price-${product.id}`}>
          {product.price?.toFixed(2)} <Pencil className="w-2.5 h-2.5 opacity-30" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-3" align="start">
        <p className="text-xs font-semibold mb-1">{t("products.selling_price_eur")}</p>
        <p className="text-xs text-muted-foreground mb-2">{t("products.selling_price_desc")}</p>
        <div className="flex items-center gap-2 mb-2">
          <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="h-8 font-mono-data text-sm" autoFocus onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }} />
          <span className="text-xs text-muted-foreground">EUR</span>
        </div>
        <Button onClick={handleSave} disabled={saving} size="sm" className="w-full h-7 bg-[#2CA8AC] text-xs">{t("common.save")}</Button>
      </PopoverContent>
    </Popover>
  );
}

function PurchasePriceCell({ product, onSaved, t }) {
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState(product.costs?.purchase_price || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/products/${product.id}/costs`, {
        purchase_price: parseFloat(price) || 0,
        shipping_cost: product.costs?.shipping_cost || 0,
        transaction_cost_pct: product.costs?.transaction_cost_pct || 2.5,
        vat_pct: product.costs?.vat_pct || 21,
      });
      toast.success(t("toast.purchase_price_saved"));
      setOpen(false);
      if (onSaved) onSaved();
    } catch { toast.error(t("toast.save_failed")); }
    finally { setSaving(false); }
  };

  const currentPrice = product.costs?.purchase_price;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`flex items-center gap-1.5 font-mono-data text-sm px-2 py-1 rounded hover:bg-accent/50 transition-colors ${currentPrice ? "" : "text-muted-foreground italic"}`}
          data-testid={`purchase-price-${product.id}`}
        >
          {currentPrice ? `${currentPrice.toFixed(2)}` : t("products.enter")}
          <Pencil className="w-3 h-3 opacity-40" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-4" align="start">
        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold mb-1">{t("products.purchase_price_label")}</p>
            <p className="text-xs text-muted-foreground mb-3">{t("products.purchase_price_desc")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
              className="h-9 font-mono-data"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
              data-testid={`purchase-price-input-${product.id}`}
            />
            <span className="text-sm text-muted-foreground shrink-0">EUR</span>
          </div>
          <Button onClick={handleSave} disabled={saving} size="sm" className="w-full bg-[#2CA8AC] hover:bg-[#248F92]" data-testid={`save-purchase-price-${product.id}`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckIcon className="w-3.5 h-3.5 mr-1.5" />{t("common.save")}</>}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ShippingCostCell({ product, onSaved, t }) {
  const [open, setOpen] = useState(false);
  const [cost, setCost] = useState(product.costs?.shipping_cost || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/products/${product.id}/costs`, {
        purchase_price: product.costs?.purchase_price || 0,
        shipping_cost: parseFloat(cost) || 0,
        transaction_cost_pct: product.costs?.transaction_cost_pct || 2.5,
        vat_pct: product.costs?.vat_pct || 21,
      });
      toast.success(t("toast.shipping_saved"));
      setOpen(false);
      if (onSaved) onSaved();
    } catch { toast.error(t("common.error")); }
    finally { setSaving(false); }
  };

  const val = product.costs?.shipping_cost;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={`flex items-center gap-1 font-mono-data text-sm px-2 py-1 rounded hover:bg-accent/50 transition-colors ${val ? "" : "text-muted-foreground italic"}`} data-testid={`shipping-cost-${product.id}`}>
          {val ? `${val.toFixed(2)}` : "-"}<Pencil className="w-2.5 h-2.5 opacity-40" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-3" align="start">
        <p className="text-xs font-semibold mb-1">{t("products.shipping_cost_label")}</p>
        <div className="flex items-center gap-2 mb-2">
          <Input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} className="h-8 font-mono-data text-sm" autoFocus onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }} />
          <span className="text-xs text-muted-foreground">EUR</span>
        </div>
        <div className="flex gap-1 mb-2 flex-wrap">
          {[0, 1.50, 2.50, 3.95].map((v) => (
            <Button key={v} variant="outline" size="sm" className="h-6 text-xs px-2 font-mono-data" onClick={() => { setCost(v); }}>
              {v === 0 ? t("products.free") : v.toFixed(2)}
            </Button>
          ))}
        </div>
        <Button onClick={handleSave} disabled={saving} size="sm" className="w-full h-7 bg-blue-600 text-xs">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : t("common.save")}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

export default function ProductsPage() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [importing, setImporting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [defaultShipOpen, setDefaultShipOpen] = useState(false);
  const [defaultShipVal, setDefaultShipVal] = useState("");
  const [scanStatus, setScanStatus] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const [newProduct, setNewProduct] = useState({ name: "", sku: "", ean: "", price: 0, stock: 0, frequency: "daily" });

  const fetchProducts = () => {
    api.get("/products").then((r) => { setProducts(r.data); setSelected(new Set()); }).catch(console.error).finally(() => setLoading(false));
  };

  const checkScanStatus = () => {
    api.get("/monitoring/status").then((r) => setScanStatus(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetchProducts(); checkScanStatus();
    api.get("/products/limit").then((r) => setLimitInfo(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!scanStatus?.scanning) return;
    const interval = setInterval(() => {
      checkScanStatus();
      fetchProducts();
    }, 10000);
    return () => clearInterval(interval);
  }, [scanStatus?.scanning]);

  const handleAdd = async () => {
    if (!newProduct.name.trim()) { toast.error(`${t("products.name")} *`); return; }
    try {
      await api.post("/products", { ...newProduct, name: newProduct.name.trim(), price: parseFloat(newProduct.price) || 0, stock: parseInt(newProduct.stock) || 0 });
      toast.success(t("toast.product_added"));
      setShowAdd(false);
      setNewProduct({ name: "", sku: "", ean: "", price: 0, stock: 0, frequency: "daily" });
      fetchProducts();
    } catch { toast.error(t("toast.add_failed")); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("products.delete_confirm"))) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success(t("toast.product_deleted"));
      fetchProducts();
    } catch { toast.error(t("toast.delete_failed")); }
  };

  const handleBulkDelete = async () => {
    try {
      const body = bulkAction === "all" ? { all: true } : { product_ids: [...selected] };
      const res = await api.post("/products/bulk-delete", body);
      toast.success(`${res.data.deleted} ${t("products.products_deleted")}`);
      setShowBulkConfirm(false);
      setBulkAction(null);
      fetchProducts();
    } catch { toast.error(t("toast.delete_failed")); }
  };

  const handleWooImport = async () => {
    setImporting(true);
    try {
      const shops = await api.get("/shops");
      const wooShop = shops.data?.find(s => s.platform === "woocommerce");
      if (!wooShop) { toast.error(t("toast.no_woo_shop")); return; }
      const res = await api.post("/woocommerce/import", { shop_id: wooShop.id });
      toast.success(`Import: ${res.data.imported} nieuw, ${res.data.updated} ${t("toast.sync_complete").toLowerCase()}`);
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || t("toast.import_failed")); }
    finally { setImporting(false); }
  };

  const handleApplyDefaultShipping = async () => {
    try {
      const res = await api.post("/products/apply-default-shipping", { shipping_cost: parseFloat(defaultShipVal) || 0 });
      toast.success(`${t("products.default_shipping")} → ${res.data.updated} ${t("analytics.products").toLowerCase()}`);
      setDefaultShipOpen(false);
      fetchProducts();
    } catch { toast.error(t("common.error")); }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((p) => p.id)));
    }
  };

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.sku || "").toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="products-page">
      {limitInfo?.over_limit > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 text-sm text-amber-900" data-testid="product-limit-banner">
          {t("products.over_limit_banner").replace("{{limit}}", limitInfo.limit).replace("{{over}}", limitInfo.over_limit)}
        </div>
      )}
      {/* Scan status banner */}
      {scanStatus?.scanning && (
        <div className="bg-[#2CA8AC]/10 border border-[#2CA8AC]/30 rounded-xl p-4 flex items-center gap-3 animate-pulse" data-testid="scan-status-banner">
          <Loader2 className="w-5 h-5 text-[#2CA8AC] animate-spin shrink-0" />
          <div>
            <p className="text-sm font-medium text-[#2CA8AC]">{t("products.scan_running")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t("products.scan_progress")}</p>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t("products.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{products.length} {t("products.title").toLowerCase()}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={handleWooImport} disabled={importing} data-testid="woo-import-btn">
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
            {t("products.woo_import")}
          </Button>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button className="bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-2" /> {t("products.add")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("products.add")}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label>{t("products.name")} *</Label><Input value={newProduct.name} onChange={(e) => setNewProduct({...newProduct, name: e.target.value})} className="mt-1" data-testid="product-name-input" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>{t("products.sku")}</Label><Input value={newProduct.sku} onChange={(e) => setNewProduct({...newProduct, sku: e.target.value})} className="mt-1" data-testid="product-sku-input" /></div>
                  <div><Label>{t("products.ean")}</Label><Input value={newProduct.ean} onChange={(e) => setNewProduct({...newProduct, ean: e.target.value})} className="mt-1" data-testid="product-ean-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>{t("products.price")} (EUR)</Label><Input type="number" step="0.01" value={newProduct.price} onChange={(e) => setNewProduct({...newProduct, price: e.target.value})} className="mt-1" data-testid="product-price-input" /></div>
                  <div><Label>{t("products.stock")}</Label><Input type="number" value={newProduct.stock} onChange={(e) => setNewProduct({...newProduct, stock: e.target.value})} className="mt-1" data-testid="product-stock-input" /></div>
                </div>
                <div className="bg-accent/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">{t("products.scan_freq_auto")}</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAdd(false)}>{t("products.cancel")}</Button>
                <Button onClick={handleAdd} className="bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="product-save-btn">{t("products.save")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search + Bulk actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("common.search")} className="pl-9" data-testid="product-search-input" />
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{selected.size} {t("products.selected")}</Badge>
            <Button variant="destructive" size="sm" onClick={() => { setBulkAction("selected"); setShowBulkConfirm(true); }} data-testid="delete-selected-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {t("products.delete_selection")}
            </Button>
          </div>
        )}
        <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => { setBulkAction("all"); setShowBulkConfirm(true); }} data-testid="delete-all-btn">
          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {t("products.delete_all")}
        </Button>
        <Popover open={defaultShipOpen} onOpenChange={setDefaultShipOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid="default-shipping-btn">
              <Truck className="w-3.5 h-3.5 mr-1.5" /> {t("products.default_shipping")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-60 p-4" align="start">
            <p className="text-sm font-semibold mb-1">{t("products.default_shipping")}</p>
            <p className="text-xs text-muted-foreground mb-3">{t("products.default_shipping_desc")}</p>
            <div className="flex items-center gap-2 mb-2">
              <Input type="number" step="0.01" value={defaultShipVal} onChange={(e) => setDefaultShipVal(e.target.value)} placeholder="0.00" className="h-8 font-mono-data" data-testid="default-shipping-input" />
              <span className="text-xs text-muted-foreground">EUR</span>
            </div>
            <Button size="sm" className="w-full bg-blue-600" onClick={handleApplyDefaultShipping} data-testid="apply-default-shipping-btn">{t("products.apply_all")}</Button>
          </PopoverContent>
        </Popover>
      </div>

      {/* Price indicator legend */}
      <div className="flex items-center gap-5 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{t("products.indicator_label")}</span>
        <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />{t("products.indicator_good_short")}</span>
        <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-amber-500" />{t("products.indicator_medium_short")}</span>
        <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-500" />{t("products.indicator_expensive_short")}</span>
        <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-blue-500" />{t("products.indicator_cheap_short")}</span>
      </div>

      {/* Bulk delete confirm dialog */}
      <Dialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-destructive" /> {t("products.delete_title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {bulkAction === "all"
              ? `${t("products.delete_all_confirm")} (${products.length})`
              : `${t("products.delete_selected_confirm")} (${selected.size})`}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkConfirm(false)}>{t("products.cancel")}</Button>
            <Button variant="destructive" onClick={handleBulkDelete} data-testid="confirm-bulk-delete-btn">
              <Trash2 className="w-4 h-4 mr-2" /> {bulkAction === "all" ? t("products.delete_all_btn") : `${selected.size} ${t("toast.delete_failed").split(" ")[0]}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Products table */}
      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                </TableHead>
                <TableHead className="w-10"></TableHead>
                <TableHead>{t("products.name")}</TableHead>
                <TableHead>{t("products.sku")}</TableHead>
                <TableHead className="text-right">{t("products.price")}</TableHead>
                <TableHead className="text-right">{t("products.purchase_price")}</TableHead>
                <TableHead className="text-right">{t("products.shipping")}</TableHead>
                <TableHead className="text-right">{t("products.margin")}</TableHead>
                <TableHead className="text-right">{t("products.competitors")}</TableHead>
                <TableHead>{t("products.frequency")}</TableHead>
                <TableHead className="text-right">{t("products.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">{t("products.no_products")}</TableCell></TableRow>
              ) : (
                filtered.map((p) => (
                  <TableRow key={p.id} className={`table-row-hover ${selected.has(p.id) ? "bg-primary/5" : ""}`} data-testid={`product-row-${p.id}`}>
                    <TableCell>
                      <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleSelect(p.id)} data-testid={`select-product-${p.id}`} />
                    </TableCell>
                    <TableCell><PriceIndicator product={p} t={t} /></TableCell>
                    <TableCell>
                      <Link to={`/dashboard/products/${p.id}`} className="font-medium hover:text-primary transition-colors" data-testid={`product-link-${p.id}`}>
                        {p.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground font-mono-data text-sm">{p.sku || "-"}</TableCell>
                    <TableCell className="text-right">
                      <SellingPriceCell product={p} onSaved={fetchProducts} t={t} />
                    </TableCell>
                    <TableCell className="text-right">
                      <PurchasePriceCell product={p} onSaved={fetchProducts} t={t} />
                    </TableCell>
                    <TableCell className="text-right">
                      <ShippingCostCell product={p} onSaved={fetchProducts} t={t} />
                    </TableCell>
                    <TableCell className="text-right">
                      {p.margin_pct !== null && p.margin_pct !== undefined ? (
                        <Badge variant={p.margin_pct >= 15 ? "default" : p.margin_pct >= 0 ? "secondary" : "destructive"}
                               className={p.margin_pct >= 15 ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : ""}>
                          {p.margin_pct}%
                        </Badge>
                      ) : <span className="text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell className="text-right">{p.competitor_count}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs capitalize">{p.frequency}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link to={`/dashboard/products/${p.id}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`view-product-${p.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                        </Link>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDelete(p.id)} data-testid={`delete-product-${p.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
